import { Link } from 'react-router';
import type { ReactNode } from 'react';

import { EmptyState, ErrorState } from '@/components/ui/empty-state';
import { cn } from '@/lib/utils';

/*
 * The list component. One table, one card list, one set of data.
 *
 * **Below 768px it stops being a table and becomes stacked cards.** That is
 * defect P6: the prototype's admin jobs and employees tables scroll sideways on
 * a narrow screen and clip the Actions column off the right edge, so the
 * buttons are unreachable. Its own client portal uses cards and reflows
 * correctly — the good pattern already existed on one surface and not the other.
 *
 * Horizontal scrolling is not the fix. A row of controls that has to be scrolled
 * to is a row of controls people do not find, and this is a tool used on a phone.
 *
 * Loading, empty and error are all handled here rather than at each call site,
 * because "handle all three" is the rule most often skipped (ROADMAP.md §4) and
 * a component that makes it hard to skip is worth more than a reminder.
 */

export interface Column<Row> {
  /** Stable key. Also the column's identity for the card layout. */
  readonly key: string;
  /** The header. 12px uppercase in the table, the label in a card. */
  readonly header: string;
  readonly render: (row: Row) => ReactNode;
  /** Hidden in the card layout — for a column whose header is decorative. */
  readonly hideLabelOnCard?: boolean;
  /** Right-aligned in the table. Actions columns usually want this. */
  readonly align?: 'left' | 'right';
  /** Kept off narrow screens entirely. Use sparingly. */
  readonly hideOnCard?: boolean;
}

export interface DataTableProps<Row> {
  /** Describes the table for a screen reader. Required — design-system.md §9.7. */
  caption: string;
  columns: readonly Column<Row>[];
  rows: readonly Row[];
  rowKey: (row: Row) => string;
  isLoading?: boolean;
  /** Set when the request failed. An error is never an empty state (P22). */
  error?: unknown;
  onRetry?: () => void;
  /** Shown when the request succeeded and there is genuinely nothing. */
  empty?: ReactNode;
  /** Makes a whole row clickable. The card layout uses it too. */
  onRowClick?: (row: Row) => void;
  /**
   * Where this row's record lives. **On a phone the whole card becomes that
   * link** — owner decision, 2026-08-06.
   *
   * The audit found the identifier links are around 20px tall against this
   * project's own 44px floor (P23, P24), and they cannot simply be made bigger:
   * they sit inside a table cell on desktop, where a 44px link would overlap
   * its neighbours. So the answer is per-layout. Desktop keeps the small link
   * and `onRowClick`; the card list, which is what a thumb actually meets,
   * makes the entire card one target well over the floor.
   *
   * Separate from `onRowClick` on purpose: a click handler cannot be a link. It
   * has no href to open in a new tab, no keyboard activation, and nothing for a
   * screen reader to announce — which is exactly how /admin/jobs ended up
   * unreachable without a mouse.
   */
  rowHref?: (row: Row) => string;
}

export function DataTable<Row>({
  caption,
  columns,
  rows,
  rowKey,
  isLoading = false,
  error,
  onRetry,
  empty,
  onRowClick,
  rowHref,
}: DataTableProps<Row>) {
  const shell = 'shadow-card overflow-hidden rounded-lg border border-ink-300 bg-white';

  if (error) {
    return (
      <div className={shell}>
        <ErrorState onRetry={onRetry} />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={shell} aria-busy="true">
        <span className="sr-only">Loading {caption}</span>
        <div aria-hidden="true" className="divide-y divide-ink-300">
          {Array.from({ length: 5 }, (_, index) => (
            <div key={index} className="flex items-center gap-4 px-4 py-4">
              <div className="h-4 flex-1 animate-pulse rounded-sm bg-ink-100" />
              <div className="h-4 w-24 animate-pulse rounded-sm bg-ink-100" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className={shell}>
        {empty ?? (
          <EmptyState title="Nothing here yet" hint="Records will appear as they arrive." />
        )}
      </div>
    );
  }

  return (
    <div className={shell}>
      {/* The table. Hidden below 768px — not scrolled, replaced. */}
      <table className="hidden w-full border-collapse text-left md:table">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr className="bg-ink-100">
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={cn(
                  'px-4 py-2.5 text-[12px] font-semibold uppercase tracking-wide text-ink-700',
                  column.align === 'right' && 'text-right',
                )}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-300">
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              onClick={
                onRowClick
                  ? () => {
                      onRowClick(row);
                    }
                  : undefined
              }
              className={cn(
                'text-body text-ink-900',
                onRowClick && 'cursor-pointer hover:bg-honey-50',
              )}
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={cn('px-4 py-3 align-middle', column.align === 'right' && 'text-right')}
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {/*
        The card list. Every column becomes a labelled row inside a card, so the
        Actions column is simply the last one down the card rather than the one
        off the right edge.
      */}
      {/*
        **The card list carries the table's own caption as its name.** The
        `<table>` above has a `<caption>`; this is the same data at a narrower
        width and had no accessible name at all, so a screen reader announced
        "list" and nothing else. It is also what lets a test target this list
        rather than the sidebar's.
      */}
      <ul aria-label={caption} className="divide-y divide-ink-300 md:hidden">
        {rows.map((row) => {
          const body = (
            <dl className="space-y-1.5">
              {columns
                .filter((column) => !column.hideOnCard)
                .map((column) => (
                  <div key={column.key} className="flex items-baseline justify-between gap-3">
                    {column.hideLabelOnCard ? null : (
                      <dt className="shrink-0 text-meta text-ink-500">{column.header}</dt>
                    )}
                    <dd
                      className={cn(
                        'min-w-0 text-body text-ink-900',
                        column.hideLabelOnCard ? 'w-full' : 'text-right',
                      )}
                    >
                      {column.render(row)}
                    </dd>
                  </div>
                ))}
            </dl>
          );

          /*
           * **The whole card is the target on a phone.** `min-h-touch` is the
           * 44px floor; in practice a card of three or more fields is well past
           * it, and the floor is what guarantees a two-field one still is.
           *
           * `block` rather than a flex row: the card's own layout is a
           * definition list and the link is a wrapper around it, not a
           * participant in it.
           */
          if (rowHref) {
            return (
              <li key={rowKey(row)}>
                <Link to={rowHref(row)} className="block min-h-touch px-4 py-3 hover:bg-honey-50">
                  {body}
                </Link>
              </li>
            );
          }

          return (
            <li
              key={rowKey(row)}
              onClick={
                onRowClick
                  ? () => {
                      onRowClick(row);
                    }
                  : undefined
              }
              className={cn('px-4 py-3', onRowClick && 'cursor-pointer hover:bg-honey-50')}
            >
              {body}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
